function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : error renders
// =============================================================================
// ---Third-party imports
import React, { Component } from 'react';
import { Table, Button, Header, Icon, Message } from 'semantic-ui-react';
// ---Framework imports
import { uix, Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { mapItems, rstrip, isString } from 'ldash';
import connect from 'uix/connect';
import { useSelector, useEffect, useState, useMemo, useActions } from 'uix/hooks';
import './error.scss';
// ---Exports
// <Message.List items={(error.traceback || []).map(e => JSON.stringify(e))} />
const silver = (s)=>/*#__PURE__*/ _jsx("span", {
        style: {
            color: 'silver'
        },
        children: s
    });
const red = (s)=>/*#__PURE__*/ _jsx("span", {
        style: {
            color: 'red',
            fontWeight: 'bold'
        },
        children: s
    });
export const ApiErrorMessage = ({ error, traceback = false })=>{
    /* UI-Element that formats error messages returned by useApi
    TODO: maybe use <TracebackDiv> here somehow
  */ return /*#__PURE__*/ _jsxs(Message, {
        negative: true,
        size: "mini",
        children: [
            /*#__PURE__*/ _jsxs(Message.Header, {
                children: [
                    red(error.error),
                    ": ",
                    /*#__PURE__*/ _jsx("tt", {
                        children: error.errorMsg
                    })
                ]
            }),
            traceback && TracebackDiv(error.traceback, '', '', {})
        ]
    });
};
export const TracebackDiv = (traceback, error, errorMsg, errorInfo)=>{
    let divs = [];
    if (!traceback) {
        divs.push(/*#__PURE__*/ _jsx("div", {
            style: {
                paddingLeft: '20px'
            },
            children: "NO TRACEBACK"
        }, "no_tr"));
    } else {
        for (let [i, e] of traceback.entries()){
            divs.push(/*#__PURE__*/ _jsxs("div", {
                style: {
                    paddingLeft: '20px'
                },
                children: [
                    `File ${e[0]}, Line `,
                    silver(e[1]),
                    `, in `,
                    silver(e[2])
                ]
            }, i * 2));
            divs.push(/*#__PURE__*/ _jsx("div", {
                style: {
                    paddingLeft: '40px'
                },
                children: `${e[3]}`
            }, i * 2 + 1));
        }
    }
    let moreInfo = mapItems(errorInfo, (k, v)=>/*#__PURE__*/ _jsxs("div", {
            children: [
                red(k),
                `: `,
                silver(isString(v) ? v : JSON.stringify(v))
            ]
        }, k));
    return /*#__PURE__*/ _jsxs("code", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                children: "Traceback (most recent call last):"
            }),
            divs,
            (error || errorMsg) && /*#__PURE__*/ _jsxs("div", {
                children: [
                    red(error),
                    `: `,
                    silver(errorMsg)
                ]
            }),
            moreInfo
        ]
    });
};
const ErrorFallbackComponent = ({ componentStack, error, customProps, relatedErrorId })=>{
    let stack = null;
    let deploy = useSelector((root)=>root.config.deploy);
    let apiurl = useSelector(Sel.apiurl);
    let request = useSelector(Sel.request);
    let reportErrorBoundary = useActions('reportErrorBoundary');
    let [stacktracey, setStackTracey] = useState(null);
    customProps = customProps || {};
    useEffect(()=>{
        import(/* webpackChunkName: 'stacktracey' */ 'stacktracey').then((module)=>{
            setStackTracey({
                StackTracey: module.default
            });
        });
    }, []);
    let enableTracy = deploy.mode != 'npm';
    let formattedStack = useMemo(()=>{
        if (stacktracey && enableTracy) {
            let _stack = new stacktracey.StackTracey(error);
            return _stack.withSources().clean().asTable();
        }
    }, [
        stacktracey,
        error
    ]);
    let errorMessage = error.toString().split('\n')[0];
    useEffect(()=>{
        // send only if non npm-dev-server
        if (enableTracy && formattedStack) {
            reportErrorBoundary({
                otid: relatedErrorId,
                errorMessage,
                errorStack: formattedStack,
                componentStack
            });
        }
    }, [
        formattedStack
    ]);
    let pre_style = {
        fontSize: '0.7em',
        margin: 0,
        lineHeight: '1.2em'
    };
    let content = /*#__PURE__*/ _jsx("i", {
        style: {
            color: 'red'
        },
        children: "See dev console for details"
    });
    if (enableTracy) {
        // in npm/dev we get
        // a) wrong line numbers
        // b) CORS errors du to webpack-internal:// protocol usage
        content = /*#__PURE__*/ _jsx("pre", {
            style: pre_style,
            children: formattedStack
        });
    }
    stack = /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("strong", {
                children: /*#__PURE__*/ _jsx("tt", {
                    children: "Stack: "
                })
            }),
            content
        ]
    });
    let location_href = apiurl.baseUrl(request.pathname);
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            height: '100%',
            overflow: 'auto',
            fontSize: 'small',
            textAlign: 'left'
        },
        ...customProps,
        children: [
            /*#__PURE__*/ _jsx("strong", {
                children: /*#__PURE__*/ _jsx("tt", {
                    children: errorMessage
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    "on ",
                    /*#__PURE__*/ _jsx("a", {
                        href: location_href,
                        children: location_href
                    }),
                    relatedErrorId && ` - [${relatedErrorId}]`
                ]
            }),
            stack,
            /*#__PURE__*/ _jsx("br", {}),
            /*#__PURE__*/ _jsxs("span", {
                children: [
                    "In",
                    /*#__PURE__*/ _jsx("i", {
                        children: /*#__PURE__*/ _jsxs("tt", {
                            children: [
                                " ",
                                customProps.name
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("pre", {
                style: pre_style,
                children: componentStack
            })
        ]
    });
};
const errorHandler = (error, componentStack)=>{
    log.warn('error during render');
// XXX: send to server
// log.error(error)
// log.error(componentStack)
};
// you can add "// @refresh reset" anywhere in the file you're editing.
// This directive is local to the file, and instructs Fast Refresh to
// remount components defined in that file on every edit.
// Dirk: XXX - explain why this is a good idea here (currently it's only a guess)
/* @refresh reset */ class _ErrorBoundary extends Component {
    static getDerivedStateFromProps(props, state) {
        if (!state || props.hmrgen > state.hmrgen || props.runId > state.runId) {
            //~ log.warn('reset ErrorBoundary')
            return {
                error: null,
                info: null,
                hmrgen: props.hmrgen,
                runId: props.runId
            };
        }
        return null;
    }
    componentDidCatch(error, info) {
        try {
            /* istanbul ignore next: Ignoring ternary; can’t reproduce missing info in test environment. */ errorHandler.call(this, error, info ? info.componentStack : '');
        } catch (ignoredError) {}
        this.setState({
            error,
            info
        });
    }
    render() {
        const { children, showFallback = true, name, fallbackProps = {}, relatedErrorId } = this.props;
        const { error, info } = this.state;
        if (error !== null) {
            log.error('render ErrorBoundary: %o', name);
            if (!showFallback) {
                return null;
            }
            return /*#__PURE__*/ _jsx(ErrorFallbackComponent, {
                componentStack: info ? info.componentStack : '',
                error: error,
                customProps: {
                    ...fallbackProps,
                    name
                },
                relatedErrorId: relatedErrorId
            });
        }
        return children || null;
    }
    constructor(props){
        super(props);
        this.state = _ErrorBoundary.getDerivedStateFromProps(props);
    }
}
// 1) we reset after runId update (goto next view)
// 2) we reset only on first HMR event (webpack)
// after the first HMR event - fast-refresh starts to handle render errors
// XXX: this does work for components that are remounted after hot-reload
// Dirk: does the above comment still holds after switch from react-hot-load to fast-refresh
_define_property(_ErrorBoundary, "storeMap", (root)=>({
        hmrgen: Sel.hmrGen(root) > 0 ? 1 : Sel.hmrGen(root),
        runId: Sel.runId(root)
    }));
export const ErrorBoundary = connect('ConnectedErrorBoundary', _ErrorBoundary);
